import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _, { chain, partial, split } from 'lodash';

import {
    KYC_STATE_VALIDATED,
    KYC_USE_CASE_WAITING_TRANSACTION
} from '../../../../services/kyc/constants';
import {
    PENDING,
    VALIDATED,
    CANCELED,
    INSUFFICIENT_FOUND,
    WAITING_TRANSACTION
} from '../../../../services/checkout/constants';
import { PATH } from '../../../../constants/path';
import { CONTEXT_ROOT } from '../../../../constants';
import { MODAL_TYPE_KYC } from '../../../../services/modal/constants';
import { ENTERPRISE_CARD_NAME, ENTERPRISE_CARD_PARTNER_EBC_ID } from '../../../../constants/ebon';

import modalService from '../../../../services/modal';
import { showModal } from '../../../../services/modal/actions';
import { fetchCheckoutDetails } from '../../../../services/checkout/actions';
import { fetchEbonOrderByIdWithCode } from '../../../../services/ebon/actions';
import { fetchUserInfo } from '../../../../services/account/myprofile/actions';
import { logoutUser } from '../../../../services/authentication/logout/actions';
import { fetchUserBalance } from '../../../../services/account/balance/actions';

import { getPartner } from '../../../../services/partner/selector';

import { formatAmountCurrency } from '../../../../utils';
import { isTokenExpired } from '../../../../services/authentication/utils';
import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';
import { displayableCashback, hasCashback } from '../../../../services/partner/utils';

import Stepper from '../Stepper';
import { Logo } from '../../../icons/Logo';
import HeadComponent from '../../../HeadComponent';
import LocalizedNavLink from '../../../router/LocalizedNavLink';
import { LoadingComponent } from '../../../loading/LoadingComponent';
import LinkButtonComponent from '../../../button/LinkButtonComponent';

import './ConfirmationEbonPage.scss';

const ConfirmationEbonPage = ({
    i18n,
    history,
    BRAND_NAME,
    BRAND_NAME_ALT,
    user,
    token,
    isAuthenticated,
    fetchEbonOrderByIdWithCode,
    fetchUserInfo,
    fetchCheckoutDetails,
    orderId,
    fetchOrder,
    partner,
    ebonDiscount,
    ebonList,
    logoutUser,
    showModal,
    checkoutSessionId,
    ebonOrderState,
    fetchingDetails,
    kycState,
    fetchUserBalance
}) => {
    const [kyc, setKyc] = useState(false);
    const [kycValidated, setKycValidated] = useState(false);
    const [pendingInterval, setPendingInterval] = useState(null);
    const [detailsInterval, setDetailsInterval] = useState(null);

    const fixed = ebonDiscount && ebonDiscount.cashback.value;
    const percent = ebonDiscount && ebonDiscount.cashback.percent.value;
    const amountCashback = fixed > 0 ? fixed : percent;

    const isCumulableWithCashback = ebonDiscount && ebonDiscount.cumulativeWithCashbackDiscount;
    const cumulFixed =
        partner && hasCashback(partner) && partner.mostDisplayableDiscountOnline.fixed;
    const cumulPercent =
        partner && hasCashback(partner) && partner.mostDisplayableDiscountOnline.percent;
    const cumulableCashback = displayableCashback(cumulPercent, cumulFixed);

    const isEnterpriseCard = partner && partner.ebcId === ENTERPRISE_CARD_PARTNER_EBC_ID;
    const partnerName = isEnterpriseCard ? ENTERPRISE_CARD_NAME : partner ? partner.name : '';

    const loading = fetchOrder || fetchingDetails || !ebonOrderState || ebonOrderState === PENDING;

    useEffect(() => {
        if (isMemberAuthenticated()) {
            fetchEbonOrderByIdWithCode(orderId);
            fetchUserInfo();
        }
    }, []);

    useEffect(() => {
        checkAuthentication();
    });

    const isMemberAuthenticated = () => !isTokenExpired(token) && isAuthenticated;

    const checkAuthentication = () => {
        if (!isMemberAuthenticated()) {
            new Promise((resolve, reject) => {
                history.push(PATH.HOME_PATH);
                resolve();
            }).then(() => logoutUser(() => showModal(modalService.constants.MODAL_TYPE_LOGIN)));
        }
    };

    const doEbonListTotal = ebonList => {
        return ebonList.reduce((acc, curr) => acc + curr.facialValue, 0);
    };

    const doEbonListTotalCashbackAmount = ebonList => {
        const ebonListTotal = doEbonListTotal(ebonList);
        const totalCashbackAmount = (ebonListTotal * amountCashback) / 100;
        const normalizedCashBackNumber = +totalCashbackAmount.toFixed(2);
        return normalizedCashBackNumber;
    };

    /* Get checkout order details */
    useEffect(() => {
        if (checkoutSessionId) {
            fetchCheckoutDetails(orderId);
        }
    }, [checkoutSessionId]);

    // Delay balance fetching because balance update is not instantaneous on server side
    useEffect(() => {
        setTimeout(() => {
            fetchUserBalance();
        }, 1000);
    }, []);

    /* Handle order states */
    useEffect(() => {
        if (!ebonOrderState || ebonOrderState === PENDING) {
            if (!pendingInterval) {
                setPendingInterval(
                    setInterval(() => {
                        fetchCheckoutDetails(orderId);
                    }, 5000)
                );
                return () => {
                    clearInterval(pendingInterval);
                };
            }
        }

        if (!kycValidated && ebonOrderState && ebonOrderState === WAITING_TRANSACTION) {
            clearInterval(pendingInterval);
            clearInterval(detailsInterval);
            setKyc(true);
            showModal(MODAL_TYPE_KYC, {
                hideCloseButton: true,
                useCase: KYC_USE_CASE_WAITING_TRANSACTION
            });
        }

        if (ebonOrderState === CANCELED || ebonOrderState === INSUFFICIENT_FOUND) {
            clearInterval(pendingInterval);
            clearInterval(detailsInterval);
            history.push(`${PATH.EBON_ERROR_PATH}-${partner.id}?paiement=error&orderId=${orderId}`);
        }

        if (ebonOrderState === VALIDATED) {
            clearInterval(pendingInterval);
            clearInterval(detailsInterval);
        }
    }, [ebonOrderState]);

    /* Start KYC verification */
    useEffect(() => {
        if (kycState === KYC_STATE_VALIDATED) {
            setKycValidated(true);
            setKyc(false);
            fetchCheckoutDetails(orderId);
        }
    }, [kycState]);

    useEffect(() => {
        if (kycValidated) {
            setDetailsInterval(
                setInterval(() => {
                    fetchCheckoutDetails(orderId);
                }, 5000)
            );
            return () => {
                clearInterval(detailsInterval);
            };
        }
    }, [kycValidated]);
    /* End KYC verification */

    const ebonListTotalCashbackAmount = ebonList && doEbonListTotalCashbackAmount(ebonList);

    return (
        <div className="template template-primary flex-container-v">
            <div className="entete hideNav" id="entete">
                <div className="flex-container-v">
                    <header className="tetedepage conteneur flex-container-h flex-center-alt">
                        <LocalizedNavLink to={PATH.HOME_PATH} title={BRAND_NAME} className="logo">
                            <Logo />
                        </LocalizedNavLink>
                        <Stepper steps={i18n.STEPS} current={4} />
                    </header>
                </div>
            </div>
            <main id="p-ebon-confirmation" className="conteneur conteneur-mobile">
                <HeadComponent
                    title={i18n.CONFIRMATION.TITLE}
                    description={i18n.DESCRIPTION}
                    canonicalLink={`${CONTEXT_ROOT}${PATH.EBON_CONFIRMATION_PATH}`}
                />
                <Stepper steps={i18n.STEPS} current={4} cssClass="mobileDisplay" />

                <div className="congratulation-container">
                    {loading && !kycValidated && <LoadingComponent />}

                    {!fetchOrder && !kyc && kycValidated && ebonOrderState !== VALIDATED && (
                        <div className="kyc-ok">
                            <h3>
                                {convertHtmlToReactDomWithParams(
                                    i18n.CONFIRMATION.WAITING_TRANSACTION.TEXT_1
                                )}
                            </h3>
                            <p>
                                {convertHtmlToReactDomWithParams(
                                    i18n.CONFIRMATION.WAITING_TRANSACTION.TEXT_2
                                )}
                            </p>
                            <div className="loader-wrapper">
                                <LoadingComponent />
                            </div>
                        </div>
                    )}

                    {!kyc && !loading && ebonOrderState && ebonOrderState === VALIDATED && (
                        <React.Fragment>
                            <h1>{i18n.CONFIRMATION.TITLE}</h1>
                            {isEnterpriseCard && (
                                <div className="enterprise-card-confirmation">
                                    <h4>
                                        {convertHtmlToReactDomWithParams(
                                            i18n.CONFIRMATION.ENTERPRISE_CARD_TEXT_1,
                                            partnerName
                                        )}
                                    </h4>
                                    <p>
                                        {convertHtmlToReactDomWithParams(
                                            i18n.CONFIRMATION.ENTERPRISE_CARD_TEXT_2,
                                            BRAND_NAME_ALT
                                        )}
                                    </p>
                                </div>
                            )}

                            <p>{i18n.CONFIRMATION.TEXT_1}</p>
                            <p>
                                {convertHtmlToReactDomWithParams(
                                    i18n.CONFIRMATION.TEXT_2,
                                    user.email
                                )}
                            </p>
                            <p>{i18n.CONFIRMATION.TEXT_3}</p>
                            <p>
                                {ebonListTotalCashbackAmount &&
                                    convertHtmlToReactDomWithParams(
                                        i18n.CONFIRMATION.TEXT_4,
                                        formatAmountCurrency(ebonListTotalCashbackAmount)
                                    )}
                            </p>
                            <p>
                                {isCumulableWithCashback &&
                                    hasCashback(partner) &&
                                    convertHtmlToReactDomWithParams(
                                        i18n.CONFIRMATION.TEXT_5,
                                        partner && partner.name,
                                        cumulableCashback
                                    )}
                            </p>

                            <div className="button-wrapper">
                                {isEnterpriseCard && (
                                    <LinkButtonComponent
                                        to={PATH.CASHBACK_EBON_MOST_POPULAR_PATH}
                                        text={i18n.CONFIRMATION.BUTTON_MOST_POPULAR}
                                        title={i18n.CONFIRMATION.BUTTON_MOST_POPULAR}
                                        color="fifth"
                                    />
                                )}
                                <LinkButtonComponent
                                    to={PATH.ACCOUNT_EBONS_PATH}
                                    text={i18n.CONFIRMATION.BUTTON}
                                    title={i18n.CONFIRMATION.BUTTON}
                                    color="main"
                                />
                            </div>
                        </React.Fragment>
                    )}
                </div>
            </main>
        </div>
    );
};

ConfirmationEbonPage.propTypes = {
    i18n: PropTypes.object,
    history: PropTypes.object,
    BRAND_NAME: PropTypes.string,
    user: PropTypes.object,
    fetchEbonOrderByIdWithCode: PropTypes.func,
    orderId: PropTypes.string,
    partner: PropTypes.object,
    ebonDiscount: PropTypes.object,
    ebonList: PropTypes.array
};

const mapStateToProps = (state, ownProps) => {
    const search = ownProps.location.search;
    const params =
        search &&
        chain(search)
            .replace('?', '') // a=toto
            .split('&') // ["a=toto","c=bobo","f=coco"]
            .map(partial(split, _, '=', 2)) // [["a","toto"],["c","bobo"],["f","coco"]]
            .fromPairs() // {"a":"toto","c":"bobo","f":"coco"}
            .value();
    const orderId = params.orderId;

    const confirmedOrder = state.ebon.confirmedOrder;
    const partner = getPartner(state, confirmedOrder.partnerId);

    return {
        i18n: state.i18n.page.EBON,
        BRAND_NAME: state.i18n.common.BRAND_NAME,
        BRAND_NAME_ALT: state.i18n.common.BRAND_NAME_ALT,
        token: state.authentication.token,
        isAuthenticated: state.authentication.isAuthenticated,
        user: state.account.user,
        confirmedOrder: confirmedOrder,
        fetchOrder: state.ebon.fetchOrder,
        ebonDiscount: confirmedOrder && state.ebon.confirmedOrder.ebonDiscount,
        ebonList: confirmedOrder && state.ebon.confirmedOrder.ebonList,
        orderId,
        partner,
        checkoutSessionId: params['cko-session-id'],
        ebonOrderState: state.checkout.details ? state.checkout.details.ebonOrderState : null,
        fetchingDetails: state.checkout.fetchingDetails,
        kycState: state.kyc.state
    };
};

const mapDispatchToProps = {
    fetchEbonOrderByIdWithCode,
    fetchUserInfo,
    logoutUser,
    showModal,
    fetchCheckoutDetails,
    fetchUserBalance
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ConfirmationEbonPage));



// WEBPACK FOOTER //
// ./src/components/pages/ebon/confirmation/ConfirmationEbonPage.js